/**
 * Datos del cliente (`profile` del lead): etiquetas, form ↔ perfil,
 * validación local y resumen de lectura. Contrato:
 * argenlotapi/docs/frontend/FRONTEND_LEAD_PROFILE.md. El adapter (normalizar,
 * diff para `updatelead`, sync de `name`, errores) vive en `leadsService.js`.
 */
import {
  LEAD_PROFILE_CHILDREN_MAX,
  LEAD_PROFILE_MIN_BIRTH_DATE,
  LEAD_PROFILE_MIN_YEAR,
  LEAD_PROFILE_NAME_MAX,
  LEAD_PROFILE_OCCUPATION_MAX,
  getLeadProfileTodayAR,
  normalizeLeadProfile,
} from '../services/leadsService';
import { LEAD_PROFILE_FIELD_LABELS } from './leadsConfig';

const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }));

export const LEAD_PROFILE_GENDER_OPTIONS = opts([
  ['female', 'Mujer'], ['male', 'Varón'], ['non_binary', 'No binario'], ['other', 'Otro'],
  ['prefer_not_to_say', 'Prefiere no decirlo'],
]);
export const LEAD_PROFILE_MARITAL_OPTIONS = opts([
  ['single', 'Soltero/a'], ['married', 'Casado/a'], ['civil_union', 'Unión convivencial'],
  ['separated', 'Separado/a'], ['divorced', 'Divorciado/a'], ['widowed', 'Viudo/a'], ['other', 'Otro'],
]);
export const LEAD_PROFILE_EMPLOYMENT_OPTIONS = opts([
  ['employee', 'Empleado/a'], ['self_employed', 'Independiente'], ['business_owner', 'Empresario/a'],
  ['retired', 'Jubilado/a'], ['unemployed', 'Sin trabajo'], ['student', 'Estudiante'], ['other', 'Otro'],
]);
export const LEAD_PROFILE_CHANNEL_OPTIONS = opts([
  ['whatsapp', 'WhatsApp'], ['phone', 'Llamada'], ['email', 'Mail'],
]);
export const LEAD_PROFILE_TIME_OPTIONS = opts([
  ['morning', 'Mañana'], ['afternoon', 'Tarde'], ['evening', 'Noche'],
]);
export const LEAD_PROFILE_HOUSING_OPTIONS = opts([
  ['first_home', 'Busca su primera vivienda'], ['owner', 'Ya es propietario/a'],
]);
export const LEAD_PROFILE_PLACE_KIND_LABELS = {
  neighborhood: 'Barrio', municipality: 'Municipio', province: 'Provincia', other: 'Otro',
};

/** Rótulos de campo (errores, timeline): viven en `leadsConfig` (sin ciclo con el servicio). */
export { LEAD_PROFILE_FIELD_LABELS };

const labelOf = (options, value) => options.find((o) => o.value === value)?.label || '';

const listText = (items) => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
};
const orText = (items) => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} o ${items[items.length - 1].toLowerCase()}`;
};

function formatBirthDate(iso) {
  const [y, m, d] = String(iso).split('-');
  return `${Number(d)}/${Number(m)}/${y}`;
}

/** Texto de la zona: `label` del catálogo, o el texto libre de «Otro». */
export function describeLeadProfilePlace(place) {
  if (!place) return '';
  return place.kind === 'other' ? place.name : (place.label || place.name);
}

/**
 * Hijos: «Sin hijos» · «2 · de 11 y 7 años (2015, 2019)» (edad aproximada por
 * año; mellizos: «2 · de 11 años (2015, 2015)») · «3 · uno de 11 años (2015)»
 * si faltan años. Los años no se deduplican (contrato).
 */
export function describeLeadProfileChildren(count, years = [], now = new Date()) {
  if (count == null) return '';
  if (count === 0) return 'Sin hijos';
  const thisYear = Number(getLeadProfileTodayAR(now).slice(0, 4));
  const sorted = [...years].sort((a, b) => a - b);
  if (!sorted.length) return String(count);
  const ages = [...new Set(sorted.map((y) => Math.max(0, thisYear - y)))];
  const agesText = `${listText(ages.map(String))} ${ages.length === 1 && ages[0] === 1 ? 'año' : 'años'}`;
  const known = sorted.length;
  const prefix = known < count ? (known === 1 ? 'uno de ' : `${known} de `) : 'de ';
  return `${count} · ${prefix}${agesText} (${sorted.join(', ')})`;
}

/** Edad aproximada por año de nacimiento («≈11 años»), o `''`. */
export function describeLeadProfileChildAge(year, now = new Date()) {
  const y = Number(year);
  const thisYear = Number(getLeadProfileTodayAR(now).slice(0, 4));
  if (!/^\d{4}$/.test(String(year || '')) || y < LEAD_PROFILE_MIN_YEAR || y > thisYear) return '';
  const age = thisYear - y;
  return `≈${age} ${age === 1 ? 'año' : 'años'}`;
}

/**
 * Lista de años del form (un campo por hijo) al cambiar la cantidad: recorta
 * los sobrantes o suma vacíos. Cantidad 0 / vacía → `[]`.
 */
export function resizeChildrenYears(years, countText) {
  const n = Number(String(countText ?? '').trim());
  const list = Array.isArray(years) ? years : [];
  if (String(countText ?? '').trim() === '' || !Number.isInteger(n) || n <= 0) return [];
  const capped = Math.min(n, LEAD_PROFILE_CHILDREN_MAX);
  return Array.from({ length: capped }, (_, i) => list[i] ?? '');
}

/**
 * Resumen compacto para la lectura: solo los campos con dato.
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function describeLeadProfile(rawProfile, now = new Date()) {
  const p = normalizeLeadProfile(rawProfile, now);
  const rows = [];
  const push = (key, label, value) => { if (value) rows.push({ key, label, value }); };
  if (p.first_name && p.last_name) push('names', 'Nombre y apellido', `${p.first_name} · ${p.last_name}`);
  else {
    push('first_name', 'Nombre de pila', p.first_name);
    push('last_name', 'Apellido', p.last_name);
  }
  if (p.birth_date) {
    const age = p.age != null ? ` · ${p.age} ${p.age === 1 ? 'año' : 'años'}` : '';
    push('birth_date', 'Nacimiento', `${formatBirthDate(p.birth_date)}${age}`);
  }
  push('gender', 'Género', labelOf(LEAD_PROFILE_GENDER_OPTIONS, p.gender));
  push('marital_status', 'Estado civil', labelOf(LEAD_PROFILE_MARITAL_OPTIONS, p.marital_status));
  push('children', 'Hijos', describeLeadProfileChildren(p.children_count, p.children_birth_years, now));
  push('work', 'Trabajo', [labelOf(LEAD_PROFILE_EMPLOYMENT_OPTIONS, p.employment_status), p.occupation]
    .filter(Boolean).join(' · '));
  push('residence_location', 'Vive en', describeLeadProfilePlace(p.residence_location));
  const channels = p.preferred_contact_channels.map((c) => labelOf(LEAD_PROFILE_CHANNEL_OPTIONS, c));
  const times = p.preferred_contact_times.map((t) => labelOf(LEAD_PROFILE_TIME_OPTIONS, t));
  const allTimes = times.length === LEAD_PROFILE_TIME_OPTIONS.length ? 'Cualquier horario' : orText(times);
  push('contact', 'Contacto', [orText(channels), allTimes].filter(Boolean).join(' · '));
  push('housing_status', 'Vivienda', labelOf(LEAD_PROFILE_HOUSING_OPTIONS, p.housing_status));
  return rows;
}

/** Perfil (normalizado o crudo) → estado del form (strings / arrays). */
export function leadProfileToForm(rawProfile) {
  const p = normalizeLeadProfile(rawProfile);
  return {
    first_name: p.first_name || '',
    last_name: p.last_name || '',
    birth_date: p.birth_date || '',
    gender: p.gender || '',
    marital_status: p.marital_status || '',
    children_count: p.children_count == null ? '' : String(p.children_count),
    // Un campo por hijo (todos opcionales): los años conocidos primero.
    children_birth_years: resizeChildrenYears(p.children_birth_years.map(String), p.children_count ?? ''),
    employment_status: p.employment_status || '',
    occupation: p.occupation || '',
    residence_location: p.residence_location,
    preferred_contact_channels: p.preferred_contact_channels,
    preferred_contact_times: p.preferred_contact_times,
    housing_status: p.housing_status || '',
  };
}

const filledYears = (years) => (Array.isArray(years) ? years : [])
  .map((t) => String(t ?? '').trim())
  .filter(Boolean);

/** Form → perfil crudo (lo normaliza `buildLeadProfilePatch`). */
export function leadProfileFromForm(form) {
  const f = form || {};
  const count = String(f.children_count ?? '').trim();
  return {
    first_name: f.first_name,
    last_name: f.last_name,
    birth_date: f.birth_date || null,
    gender: f.gender || null,
    marital_status: f.marital_status || null,
    children_count: count === '' ? null : Number(count),
    children_birth_years: filledYears(f.children_birth_years).map(Number),
    employment_status: f.employment_status || null,
    occupation: f.occupation,
    residence_location: f.residence_location || null,
    preferred_contact_channels: f.preferred_contact_channels || [],
    preferred_contact_times: f.preferred_contact_times || [],
    housing_status: f.housing_status || null,
  };
}

/**
 * Validación local (los mismos límites que la API, para no esperar el 400).
 * @returns {Record<string, string>} campo → mensaje (vacío = ok).
 */
export function validateLeadProfileForm(form, now = new Date()) {
  const f = form || {};
  const errors = {};
  const today = getLeadProfileTodayAR(now);
  const thisYear = Number(today.slice(0, 4));
  if (String(f.first_name || '').trim().length > LEAD_PROFILE_NAME_MAX) errors.first_name = `Hasta ${LEAD_PROFILE_NAME_MAX} caracteres.`;
  if (String(f.last_name || '').trim().length > LEAD_PROFILE_NAME_MAX) errors.last_name = `Hasta ${LEAD_PROFILE_NAME_MAX} caracteres.`;
  const birth = String(f.birth_date || '').trim();
  if (birth) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birth)) errors.birth_date = 'Fecha inválida.';
    else if (birth > today) errors.birth_date = 'La fecha de nacimiento no puede ser futura.';
    else if (birth < LEAD_PROFILE_MIN_BIRTH_DATE) errors.birth_date = 'La fecha de nacimiento no puede ser anterior a 1900.';
  }
  const countText = String(f.children_count ?? '').trim();
  const count = countText === '' ? null : Number(countText);
  if (countText !== '' && !(Number.isInteger(count) && count >= 0 && count <= LEAD_PROFILE_CHILDREN_MAX)) {
    errors.children_count = `Un número entre 0 y ${LEAD_PROFILE_CHILDREN_MAX}.`;
  }
  const years = filledYears(f.children_birth_years);
  if (years.length) {
    const bad = years.some((y) => !/^\d{4}$/.test(y) || Number(y) < LEAD_PROFILE_MIN_YEAR || Number(y) > thisYear);
    if (bad) errors.children_birth_years = `Cada año entre ${LEAD_PROFILE_MIN_YEAR} y ${thisYear}.`;
    else if (count == null || errors.children_count) errors.children_birth_years = 'Cargá la cantidad de hijos antes de los años.';
    else if (years.length > count) errors.children_birth_years = 'Hay más años que hijos.';
  }
  if (String(f.occupation || '').trim().length > LEAD_PROFILE_OCCUPATION_MAX) {
    errors.occupation = `Hasta ${LEAD_PROFILE_OCCUPATION_MAX} caracteres.`;
  }
  return errors;
}
